import React, { useState, useEffect, useMemo } from 'react';
import { FlatList, ScrollView, SectionList, TouchableOpacity, View } from 'react-native'
import moment from 'moment';

import api from "../../../../../utils/api"
import tw from '../../../../../styles/tailwind';
import Text from '../../../../../components/Text';
import Icon from '../../../../../components/IcomoonIcon';
import { Image } from 'expo-image';
import { useQuery } from '@tanstack/react-query';
import { MonthSelectorModal } from '../../../../../components/Forms/MonthSelector';


export const currency = (value, withDecimalsOnZero=true) => `${value ? value?.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' }) : '-'}`

// Monto que siempre muestra la moneda (incluido $0.00).
export const money = (value) => (value ?? 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]


export default function ReportsSection({ communityId }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showMonthSelectorModal, setShowMonthSelectorModal] = useState(false);

  const monthISO = selectedMonth.toISOString().split("T")[0]

  // NOTA: el backend aún no filtra por mes (con ?month= devuelve ceros). Por eso va sin
  // parámetro y muestra las cifras reales. El selector cambia la etiqueta pero no filtra
  // hasta que el backend soporte el mes (ver NOTAS-DEV, punto K).
  const { isInitialLoading, error, data: reportOverview, refetch } = useQuery({
    queryKey: ['community.accounting.reports', communityId],
    queryFn: () => api.get(`/communities/${communityId}/accounting/reports/overview`).then(response => response.data),
  })

  const periodLabel = `${MONTHS[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`

  const expensesByCategory = reportOverview?.expenses_by_category ?? []


  return (
    <ScrollView style={tw`flex-1 bg-[#F3F2F3]`} contentContainerStyle={tw`px-4 py-4 pb-10`}>
      {
        isInitialLoading &&
        <Text style={tw`my-8 text-lg text-center text-gray-400`}>Cargando...</Text>
      }

      {/* Resumen del periodo */}
      <View style={tw`bg-secondary rounded-2xl px-5 py-5 mb-4`}>
        <TouchableOpacity
          style={tw`row justify-between items-center bg-white/10 rounded-xl px-4 py-3 mb-4`}
          onPress={() => setShowMonthSelectorModal(true)}
        >
          <Text style={tw`text-white font-500`}>{periodLabel}</Text>
          <Icon name='chevron-right' size={13} color={tw.color('white')} style={{ transform: [{ rotateZ: '90deg' }] }} />
        </TouchableOpacity>

        <Text style={tw`text-white/90`}>Balance del periodo</Text>
        <Text style={tw.style(`text-white font-700 text-4xl`, reportOverview?.balance_total < 0 && `text-red-300`)}>
          {money(reportOverview?.balance_total)}
        </Text>

        <View style={tw`border-t border-white/15 mt-4 pt-3 gap-y-2`}>
          <View style={tw`row justify-between`}>
            <Text style={tw`text-white/80`}>+ Ingresos del periodo</Text>
            <Text style={tw`text-white font-600`}>{money(reportOverview?.income_total)}</Text>
          </View>
          <View style={tw`row justify-between`}>
            <Text style={tw`text-white/80`}>- Gastos del periodo</Text>
            <Text style={tw`text-white font-600`}>{money(reportOverview?.expenses_total)}</Text>
          </View>
        </View>
      </View>

      {/* Ingresos */}
      <View style={tw`bg-[#EAFBF9] border border-primary rounded-2xl px-5 py-4 mb-4`}>
        <Text style={tw`text-secondary font-600 text-xl mb-3`}>Ingresos</Text>

        <View style={tw`row justify-between mb-2`}>
          <Text style={tw`text-gray-600`}>Mantenimiento</Text>
          <Text style={tw`text-gray-800 font-500`}>{money(reportOverview?.incomes_by_category?.main)}</Text>
        </View>

        <View style={tw`row justify-between mb-2`}>
          <Text style={tw`text-gray-600`}>Cuotas adicionales</Text>
          <Text style={tw`text-gray-800 font-500`}>{money(reportOverview?.incomes_by_category?.other)}</Text>
        </View>

        <View style={tw`row justify-between border-t border-primary/30 mt-1 pt-2`}>
          <Text style={tw`text-secondary font-600`}>Total</Text>
          <Text style={tw`text-secondary font-700`}>{money(reportOverview?.income_total)}</Text>
        </View>
      </View>

      {/* Gastos */}
      <View style={tw`bg-[#FBF5FF] border border-secondary/20 rounded-2xl px-5 py-4 mb-4`}>
        <Text style={tw`text-gray-800 font-600 text-xl mb-3`}>Gastos</Text>

        {
          expensesByCategory.map(item =>
            <View style={tw`row justify-between mb-2`} key={item.name}>
              <Text style={tw`text-gray-600`}>{item.name}</Text>
              <Text style={tw`text-gray-800 font-500`}>{money(item.total)}</Text>
            </View>
          )
        }

        {
          expensesByCategory.length === 0 &&
            <Text style={tw`text-gray-400 mb-2`}>Sin gastos registrados</Text>
        }

        <View style={tw`row justify-between border-t border-secondary/20 mt-1 pt-2`}>
          <Text style={tw`text-gray-800 font-600`}>Total</Text>
          <Text style={tw`text-gray-800 font-700`}>{money(reportOverview?.expenses_total)}</Text>
        </View>
      </View>

      {
        showMonthSelectorModal &&
          <MonthSelectorModal
            onRequestClose={() => setShowMonthSelectorModal(false)}
            selectedDate={selectedMonth}
            maxDate={moment()}
            onChange={date => {
              setShowMonthSelectorModal(false)
              setSelectedMonth(date)
            }}
          />
      }
    </ScrollView>
  )
}